// src/pages/LoginPage.js
//
// Login screen
// ============
//
// PURPOSE
// -------
// User-facing login page for the internal SPA.
//
// This page now supports ONLY:
//
// 1) normal login form
//
// IMPORTANT SECURITY DECISION
// ---------------------------
// This page is aligned with the application's STRICT TAB-SELECTED AUTH policy.
//
// That means:
//
// - no "Continue as current user" chooser
// - no "Sign in as different user in this tab" chooser flow
// - no recovery-aware session handoff UI
// - no duplicate-tab recovery UI
// - no manual URL recovery UI
//
// If a tab is opened manually, duplicated, restored after a crash,
// or loses its tab-local runtime/session context,
// the user must simply log in again in that tab.
//
// WHY THIS IS SAFER
// -----------------
// This is the conservative and preferred behavior for:
//
// - shared computers
// - environments where many users may use the same physical device
// - broken frontend runtime after deployment/update
// - situations where a stale or ambiguous tab must NOT expose
//   another user's session choice UI
//
// IMPORTANT ROUTER NOTE
// ---------------------
// App uses BrowserRouter basename="/app".
// Therefore React Router paths here must remain INTERNAL:
//
// - "/"
// - "/dashboard"
// - "/register"
//
// Never prefix routes manually with "/app".
//
// PUBLIC i18n STRATEGY
// --------------------
// Login is a public page. At this point there may be no authenticated user and
// therefore no user profile from which we could read preferred_language.
//
// The login page separates two concepts:
//
// 1) display language of the public login form
//    - initialized globally from browser/OS language,
//    - can be changed manually on the page,
//    - is not persisted to localStorage/sessionStorage.
//
// 2) post-login language mode
//    - default: Profile language,
//    - after successful login AuthContext applies user.preferred_language,
//    - if the user explicitly selects Polish/English, that manual language wins
//      for the current authenticated SPA session without changing the profile.
//
// This keeps the login UX explicit and avoids guessing before authentication.
//

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../context/AuthContext";
import PublicLanguageDropdown, {
  PROFILE_LANGUAGE_VALUE,
  PUBLIC_LANGUAGE_MODES,
} from "../components/PublicLanguageDropdown";

import "../styles/FormStyles.css";
import "./LoginPage.css";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Error helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Convert backend / transport errors into human-readable login messages.
 *
 * Why keep this logic centralized?
 * - cleaner submit handler
 * - easier consistency across future auth pages
 * - easier adjustment if backend error contract changes later
 *
 * i18n note:
 * - backend-provided messages are preserved as backend payload content,
 * - frontend-owned fallback messages are translated through the auth namespace.
 */
function getLoginErrorMessage(err, t) {
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.error || err?.response?.data?.message;

  if (typeof status === "number") {
    if (status === 429) {
      return serverMsg || t("login.errors.tooManyAttempts");
    }

    if (status === 403) {
      return serverMsg || t("login.errors.inactive");
    }

    if (status === 401) {
      return serverMsg || t("login.errors.invalidCredentials");
    }

    return serverMsg || t("login.errors.failedWithStatus", { status });
  }

  const msg = String(err?.message || "").trim();
  if (!msg) {
    return t("login.errors.unexpected");
  }

  const lowered = msg.toLowerCase();

  if (lowered.includes("429") || lowered.includes("too many")) {
    return t("login.errors.tooManyAttempts");
  }

  if (lowered.includes("not approved") || lowered.includes("inactive")) {
    return t("login.errors.inactive");
  }

  if (lowered.includes("invalid credentials")) {
    return t("login.errors.invalidCredentials");
  }

  return msg;
}

/* -------------------------------------------------------------------------- */
/* Redirect helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Decide where the user should go after successful login.
 *
 * We accept a route passed through router state when it is safe and meaningful,
 * otherwise we fall back to "/dashboard".
 *
 * This protects against awkward flows such as:
 * - redirecting back to login page itself
 * - redirecting to public/legal pages after login
 * - malformed external-looking route values
 *
 * IMPORTANT FOR STRICT TAB-SELECTED AUTH
 * --------------------------------------
 * This helper is also what enables the desired UX for:
 * - manual protected URL entry
 * - redirect to login by ProtectedRoute
 * - successful login
 * - return back to originally requested protected route
 *
 * So for example:
 *   user enters /app/work-items/todo:15 manually
 *   -> ProtectedRoute redirects to "/"
 *      with state.from="/work-items/todo:15"
 *   -> successful login returns to "/work-items/todo:15"
 */
function getSafePostLoginTarget(location) {
  const fallback = "/dashboard";

  const candidate =
    location?.state?.from?.pathname ||
    location?.state?.from ||
    fallback;

  const path = String(candidate || "").trim();

  if (!path.startsWith("/")) return fallback;

  const blockedTargets = new Set([
    "/",
    "/register",
    "/shop",
    "/shop/thank-you",
    "/polityka-prywatnosci",
    "/regulamin",
  ]);

  if (blockedTargets.has(path)) {
    return fallback;
  }

  return path;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const LoginPage = () => {
  const { t, i18n } = useTranslation("auth");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /**
   * Login language decision for the current submit only.
   *
   * Default is Profile language. This is a mode, not a concrete language: after
   * successful authentication AuthContext uses user.preferred_language returned
   * by the backend.
   *
   * Manual Polish/English selections immediately change the public login form
   * display language and are then passed to AuthContext as an explicit
   * current-session override. Nothing is written to browser storage here.
   */
  const [loginLanguageMode, setLoginLanguageMode] = useState(
    PUBLIC_LANGUAGE_MODES.PROFILE
  );
  const [manualLoginLanguage, setManualLoginLanguage] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const {
    handleLogin,
    authState,
    authChecked,
    loading: authLoading,
  } = useContext(AuthContext);

  /* ------------------------------------------------------------------------ */
  /* Body style normalization                                                 */
  /* ------------------------------------------------------------------------ */

  /**
   * Defensive body-style normalization for the public auth shell.
   *
   * Why this is still useful even though App.js now adds public body classes:
   * - Login/Register are premium public pages and intentionally do not use the
   *   authenticated backoffice body scale.
   * - Internal shell layout stabilization introduced compensated 111.11vw /
   *   111.11vh dimensions for the scaled authenticated shell.
   * - If LoginPage disables only `transform` but leaves compensated min-width or
   *   height in place, the background image can become wider than the real
   *   browser viewport and visually drift to the right.
   *
   * Therefore this page explicitly normalizes all critical body inline styles
   * while it is mounted. The values are restored on unmount, so internal shell
   * and public shop routes keep their own geometry.
   */
  useEffect(() => {
    const previousBodyStyles = {
      transform: document.body.style.transform,
      transformOrigin: document.body.style.transformOrigin,
      width: document.body.style.width,
      minWidth: document.body.style.minWidth,
      maxWidth: document.body.style.maxWidth,
      height: document.body.style.height,
      minHeight: document.body.style.minHeight,
      maxHeight: document.body.style.maxHeight,
      overflow: document.body.style.overflow,
    };

    document.body.style.transform = "none";
    document.body.style.transformOrigin = "top left";
    document.body.style.width = "100vw";
    document.body.style.minWidth = "100vw";
    document.body.style.maxWidth = "100vw";
    document.body.style.height = "100vh";
    document.body.style.minHeight = "100vh";
    document.body.style.maxHeight = "100vh";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.transform = previousBodyStyles.transform;
      document.body.style.transformOrigin = previousBodyStyles.transformOrigin;
      document.body.style.width = previousBodyStyles.width;
      document.body.style.minWidth = previousBodyStyles.minWidth;
      document.body.style.maxWidth = previousBodyStyles.maxWidth;
      document.body.style.height = previousBodyStyles.height;
      document.body.style.minHeight = previousBodyStyles.minHeight;
      document.body.style.maxHeight = previousBodyStyles.maxHeight;
      document.body.style.overflow = previousBodyStyles.overflow;
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Background image path                                                    */
  /* ------------------------------------------------------------------------ */

  /**
   * Build background image path relative to PUBLIC_URL.
   *
   * This keeps deployment path handling safe when the app is served under /app.
   */
  const loginBgCssVar = useMemo(() => {
    const base = process.env.PUBLIC_URL || "";
    const path = `${base}/login-bg.png`.replace(/\/{2,}/g, "/");
    return `url("${path}")`;
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Post-login route                                                         */
  /* ------------------------------------------------------------------------ */

  const postLoginTarget = useMemo(() => {
    return getSafePostLoginTarget(location);
  }, [location]);

  /* ------------------------------------------------------------------------ */
  /* Already-authenticated redirect                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * In the strict tab-selected variant we do not show any chooser UI.
   *
   * Therefore if auth bootstrap finishes and user is already authenticated
   * in THIS tab, we simply redirect to the appropriate post-login target.
   *
   * This keeps the login page behavior simple and deterministic.
   */
  useEffect(() => {
    if (!authChecked) return;
    if (!authState?.isAuthenticated) return;

    navigate(postLoginTarget, { replace: true });
  }, [authChecked, authState?.isAuthenticated, navigate, postLoginTarget]);


  /* ------------------------------------------------------------------------ */
  /* Public login language selection                                          */
  /* ------------------------------------------------------------------------ */

  const languageDropdownValue =
    loginLanguageMode === PUBLIC_LANGUAGE_MODES.PROFILE
      ? PROFILE_LANGUAGE_VALUE
      : manualLoginLanguage;

  const onLoginLanguageChange = useCallback(
    async (selection) => {
      if (selection?.mode === PUBLIC_LANGUAGE_MODES.PROFILE) {
        setLoginLanguageMode(PUBLIC_LANGUAGE_MODES.PROFILE);
        setManualLoginLanguage(null);
        return;
      }

      const nextLanguage = selection?.language;
      setLoginLanguageMode(PUBLIC_LANGUAGE_MODES.MANUAL);
      setManualLoginLanguage(nextLanguage);

      if (nextLanguage && i18n.language !== nextLanguage) {
        await i18n.changeLanguage(nextLanguage);
      }
    },
    [i18n]
  );

  /* ------------------------------------------------------------------------ */
  /* Submit handler                                                           */
  /* ------------------------------------------------------------------------ */

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      setError("");
      setSubmitting(true);

      try {
        const cleanUsername = username.trim();
        const rawPassword = password;

        const ok = await handleLogin(cleanUsername, rawPassword, {
          languageMode: loginLanguageMode,
          manualLanguage: manualLoginLanguage,
        });

        if (ok) {
          navigate(postLoginTarget, { replace: true });
          return;
        }

        setError(t("login.errors.failedGeneric"));
      } catch (err) {
        const message = getLoginErrorMessage(err, t);

        if (DEV) {
          // eslint-disable-next-line no-console
          console.warn("[LoginPage] login failed (dev):", err);
        }

        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      username,
      password,
      handleLogin,
      loginLanguageMode,
      manualLoginLanguage,
      navigate,
      postLoginTarget,
      t,
    ]
  );

  /* ------------------------------------------------------------------------ */
  /* Form state                                                               */
  /* ------------------------------------------------------------------------ */

  /**
   * Submit button should be enabled only when:
   * - username is non-empty after trimming
   * - password is present
   * - local submit is not already in progress
   * - auth bootstrap is not currently busy
   */
  const canSubmit =
    username.trim().length > 0 &&
    password.length > 0 &&
    !submitting &&
    !authLoading;

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="login-page" style={{ ["--login-bg"]: loginBgCssVar }}>
      <div className="login-form-container">
        <PublicLanguageDropdown
          allowProfileLanguage
          value={languageDropdownValue}
          onChange={onLoginLanguageChange}
        />

        <h1>{t("login.title")}</h1>

        {!authChecked ? (
          <p className="register-link" style={{ marginBottom: 16 }}>
            {t("login.sessionChecking")}
          </p>
        ) : (
          <form className="form" onSubmit={onSubmit} noValidate>
            <label htmlFor="username" className="label">
              {t("login.fields.username")}
            </label>

            <input
              id="username"
              type="text"
              placeholder={t("login.placeholders.username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input"
              required
              autoComplete="username"
              disabled={submitting}
            />

            <label htmlFor="password" className="label">
              {t("login.fields.password")}
            </label>

            <input
              id="password"
              type="password"
              placeholder={t("login.placeholders.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              autoComplete="current-password"
              disabled={submitting}
            />

            <button
              type="submit"
              className="button"
              disabled={!canSubmit}
              aria-busy={submitting ? "true" : "false"}
            >
              {submitting ? t("login.actions.loggingIn") : t("login.actions.login")}
            </button>
          </form>
        )}

        {error && (
          <p className="error" role="alert" aria-live="polite">
            {error}
          </p>
        )}

        <p className="register-link">
          {t("login.registerPrompt")} {" "}
          <Link to="/register">{t("login.registerLink")}</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;


